import { CSSProperties, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * ChatNotice — a centered interstitial inside a message stream for moments
 * that pause the conversation: a figure (optionally breathing/pulsing), a
 * serif message, and an optional gold action button.
 *
 * Covers Alma's "finalizing" state (pulse + message) and the retryable
 * failure state (message + action).
 */
export interface ChatNoticeProps {
  /** Figure node (e.g. an <img> avatar or <Avatar/>). */
  figure?: ReactNode;
  /** Gentle scale/opacity pulse on the figure ("thinking"). */
  pulse?: boolean;
  /** Serif headline above the message — for figure-led notices with their own title. */
  headline?: string;
  /**
   * Marks a long-running headline (e.g. "Finding your host..." while we're
   * still waiting on a slow request): a low-opacity duplicate of the full
   * text sits still underneath, while the real letters fade in one by one
   * (staggered), hold, fade back out, and repeat — looping forever.
   */
  headlineLoading?: boolean;
  /** Headline scale — 'lg' is the default page-title size; 'sm' drops to card-title size. */
  headlineSize?: 'lg' | 'sm';
  /** Let the headline wrap across lines at its natural size instead of the
   *  default single-line shrink-to-fit — for a full sentence headline meant
   *  to read at a fixed size rather than compress to stay on one line. */
  headlineWrap?: boolean;
  /** Serif message (finalizing) — or quiet sans message when `action` is set,
   *  or when `messageVariant="caption"`. Optional when `headline` carries the notice alone. */
  message?: string;
  /** 'serif' (default) is the 17px serif paragraph. 'caption' is the quiet
   *  13.5px sans style — the same one `action` notices already use — for a
   *  headline's supporting caption line. */
  messageVariant?: 'serif' | 'caption';
  /** Gold pill action label (e.g. "Try again"). */
  actionLabel?: string;
  onAction?: () => void;
  /** Move focus to the action button when the notice appears (it is the next step). Read once, on mount. */
  focusAction?: boolean;
  style?: CSSProperties;
}

const NOTICE_LETTER_CYCLE_S = 1.8;
const NOTICE_LETTER_STAGGER_S = 0.07;

/**
 * Measures its nowrap content against the available width and scales it
 * down (uniformly, via transform) just enough to keep it on one line —
 * headlines never wrap, they shrink instead, on any viewport.
 */
function FitHeadline({ children, deps }: { children: ReactNode; deps: unknown[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;
    const measure = () => {
      const containerWidth = container.clientWidth;
      const contentWidth = content.scrollWidth;
      setScale(contentWidth > containerWidth && contentWidth > 0 ? containerWidth / contentWidth : 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return (
    <div ref={containerRef} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <div ref={contentRef} style={{ display: 'inline-block', whiteSpace: 'nowrap', transform: `scale(${scale})` }}>
        {children}
      </div>
    </div>
  );
}

/**
 * A static low-opacity duplicate of the headline sits underneath; on top,
 * each letter fades in with a staggered delay (same shape as a one-shot
 * staggered-fade-in), holds, fades back out, and the cycle repeats forever
 * — a staggered fade in loop, reading as a loading cue.
 */
function LoadingHeadline({ text, style }: { text: string; style: CSSProperties }) {
  return (
    <div style={{ position: 'relative' }}>
      <style>{
        '@keyframes sk-notice-letter-fade{0%,100%{opacity:0}15%,60%{opacity:1}80%{opacity:0}}'
      }</style>
      <p aria-hidden="true" style={{ ...style, position: 'absolute', inset: 0, opacity: .15, margin: 0 }}>{text}</p>
      <p style={{ ...style, position: 'relative', margin: 0 }}>
        {text.split('').map((char, i) => (
          <span
            key={i}
            style={{
              opacity: 0,
              animation: `sk-notice-letter-fade ${NOTICE_LETTER_CYCLE_S}s ease-in-out ${(i * NOTICE_LETTER_STAGGER_S).toFixed(3)}s infinite`,
            }}
          >
            {char}
          </span>
        ))}
      </p>
    </div>
  );
}

export function ChatNotice({ figure, pulse = false, headline, headlineLoading = false, headlineSize = 'lg', headlineWrap = false, message, messageVariant = 'serif', actionLabel, onAction, focusAction = false, style }: ChatNoticeProps) {
  const [hover, setHover] = useState(false);
  const isAction = !!actionLabel;
  const actionRef = useRef<HTMLButtonElement>(null);
  // Only as the notice appears: never pull focus away from something typed later.
  useEffect(() => {
    if (focusAction) actionRef.current?.focus({ preventScroll: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const headlineStyle: CSSProperties = headlineSize === 'sm'
    ? { fontFamily: 'var(--font-serif)', color: 'var(--text)', fontSize: 'var(--fs-card-title-lg)', lineHeight: 'var(--lh-card-title)' }
    : { fontFamily: 'var(--font-serif)', color: 'var(--text)', fontSize: 'var(--fs-page-title)', lineHeight: 'var(--lh-page-title)', letterSpacing: 'var(--ls-page-title)' };
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: isAction ? 14 : 16, padding: '48px 20px', textAlign: 'center',
        fontFamily: 'var(--font-sans)',
        animation: 'sk-notice-fade 1.2s ease both',
        ...style,
      }}
    >
      <style>{'@keyframes sk-notice-pulse{0%,100%{opacity:.7;transform:scale(1)}50%{opacity:1;transform:scale(1.04)}}@keyframes sk-notice-fade{from{opacity:0}to{opacity:1}}'}</style>
      {figure ? (
        <div style={{ animation: pulse ? 'sk-notice-pulse 2s ease-in-out infinite' : undefined }}>{figure}</div>
      ) : null}
      {headline ? (
        headlineWrap ? (
          <p style={{ ...headlineStyle, margin: 0, maxWidth: 320, textWrap: 'pretty' }}>
            {headline}
          </p>
        ) : (
          <FitHeadline deps={[headline, headlineLoading]}>
            {headlineLoading ? (
              <LoadingHeadline text={headline} style={headlineStyle} />
            ) : (
              <p style={{ ...headlineStyle, margin: 0 }}>
                {headline}
              </p>
            )}
          </FitHeadline>
        )
      ) : null}
      {message ? (
        <p
          style={isAction || messageVariant === 'caption' ? {
            fontSize: 13.5, color: 'var(--text-soft)', maxWidth: 280, lineHeight: 1.65, textWrap: 'pretty',
          } : {
            fontFamily: 'var(--font-serif)', fontSize: 17, color: 'var(--text-soft)', lineHeight: 1.6, maxWidth: 280, textWrap: 'pretty',
          }}
        >
          {message}
        </p>
      ) : null}
      {isAction ? (
        <button
          ref={actionRef}
          type="button"
          onClick={onAction}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            background: 'rgb(var(--gold))', color: 'rgb(var(--ink))',
            fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 13,
            border: 'none', borderRadius: 999, padding: '11px 24px', cursor: 'pointer',
            transition: 'filter .15s', filter: hover ? 'brightness(1.06)' : 'none',
          }}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

export default ChatNotice;
